import React from 'react';
import { CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import { AppBuilderProgressBarProps, AppBuilderProjectStatus } from '../types/appbuilder';

const STEPS = [
  { threshold: 15, label: 'Analysing' },
  { threshold: 30, label: 'Processing' },
  { threshold: 65, label: 'Generating' },
  { threshold: 75, label: 'Committing' },
  { threshold: 90, label: 'Deploying' },
  { threshold: 100, label: 'Complete' },
];

const AppBuilderProgressBar: React.FC<AppBuilderProgressBarProps> = ({
  progress,
  status,
  message,
  animated = true,
}) => {
  const isFailed = status === AppBuilderProjectStatus.FAILED;
  const isCompleted = status === AppBuilderProjectStatus.COMPLETED;
  const clamped = Math.min(100, Math.max(0, progress));

  return (
    <div className="ed-build">
      <div className="ed-build-head">
        <div className="ed-build-head-left">
          {isCompleted ? (
            <CheckCircle size={20} />
          ) : isFailed ? (
            <AlertTriangle size={20} />
          ) : (
            <Loader size={20} className={animated ? 'ed-icon-spin' : undefined} />
          )}
          <div>
            <span className="ed-kicker">{status}</span>
            <p className="ed-body" style={{ marginTop: 4 }}>
              {message || 'Processing…'}
            </p>
          </div>
        </div>

        <span className="ed-build-pct">{clamped}%</span>
      </div>

      <div
        className="ed-build-track"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Prototype generation progress"
      >
        <div
          className={`ed-build-fill${isFailed ? ' is-failed' : ''}`}
          style={{ width: `${clamped}%` }}
        />
      </div>

      <div className="ed-build-steps">
        {STEPS.map(({ threshold, label }) => (
          <div
            key={label}
            className={`ed-build-step${clamped >= threshold ? ' is-reached' : ''}`}
          >
            <span className="ed-build-dot" />
            <span className="ed-build-step-label">{label}</span>
          </div>
        ))}
      </div>

      {isFailed && (
        <div className="ed-build-note is-err" role="alert">
          <AlertTriangle size={15} />
          <span>Generation failed. Try again with a slightly different brief.</span>
        </div>
      )}

      {isCompleted && (
        <div className="ed-build-note is-ok">
          <CheckCircle size={15} />
          <span>Your prototype is ready.</span>
        </div>
      )}
    </div>
  );
};

export default AppBuilderProgressBar;
